import React, { useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  KeyboardArrowDown,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  HOME_ROUTE_PATH,
  LOGIN_ROUTE_PATH,
  USER_PROFILE_ROUTE_PATH,
} from "../../utils/constants/AppConstants";
import { useAuth } from "../../auth/AuthContext";
import { useUserProfileContext } from "../../contexts/ProfileContext";

const LoggedInProfileMenuComponent: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { logout } = useAuth();
  const { userProfile } = useUserProfileContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const userName = userProfile?.name || "User";
  const firstName = userName.split(" ")[0];
  const displayName = firstName.length > 20 ? `${firstName.substring(0, 20)}...` : firstName;

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const goToProfile = () => {
    navigate(USER_PROFILE_ROUTE_PATH);
    closeMenu();
  };

  const goToDashboard = () => {
    navigate(HOME_ROUTE_PATH);
    closeMenu();
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Keep navigation behavior even if logout request fails.
    } finally {
      navigate(LOGIN_ROUTE_PATH);
      closeMenu();
    }
  };

  return (
    <>
      <Box
        onClick={openMenu}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: { xs: 1.35, sm: 1.65 },
          py: { xs: 0.55, sm: 0.65 },
          borderRadius: 999,
          border: `1px solid ${alpha(theme.palette.text.secondary, 0.22)}`,
          backgroundColor: isOpen ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.background.paper, 0.45),
          cursor: "pointer",
          transition: "all 0.18s ease",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
            borderColor: alpha(theme.palette.primary.main, 0.35),
          },
        }}
      >
        <Avatar
          sx={{
            width: { xs: 28, sm: 30 },
            height: { xs: 28, sm: 30 },
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            fontSize: { xs: "0.82rem", sm: "0.88rem" },
            fontWeight: 700,
          }}
        >
          {userName.charAt(0).toUpperCase()}
        </Avatar>

        <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0, maxWidth: 120 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              lineHeight: 1.15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={userName}
          >
            {displayName}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, lineHeight: 1.1 }}>
            Personal workspace
          </Typography>
        </Box>

        <IconButton
          size="small"
          sx={{ color: theme.palette.text.secondary, p: 0.2, display: { xs: "none", sm: "inline-flex" } }}
          aria-label="Open account menu"
        >
          <KeyboardArrowDown />
        </IconButton>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 268,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.text.secondary, 0.2)}`,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: "blur(10px)",
            boxShadow: "0 18px 42px rgba(2,6,23,0.45)",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.75, pb: 1.4 }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Avatar
              sx={{
                width: 30,
                height: 30,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontSize: "0.8rem",
                fontWeight: 700,
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {displayName}
              </Typography>
              <Chip
                size="small"
                label="Signed in"
                sx={{
                  mt: 0.6,
                  height: 18,
                  borderRadius: 999,
                  color: theme.palette.success.light,
                  backgroundColor: alpha(theme.palette.success.main, 0.18),
                }}
              />
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ borderColor: alpha(theme.palette.text.secondary, 0.16) }} />

        <MenuItem
          onClick={goToDashboard}
          sx={{ py: 1.25, px: 2, "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.12) } }}
        >
          <ListItemIcon sx={{ color: theme.palette.primary.light, minWidth: 30 }}>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Dashboard"
            secondary="Overview and insights"
            primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "0.75rem", color: "text.secondary" }}
          />
        </MenuItem>

        <MenuItem
          onClick={goToProfile}
          sx={{ py: 1.25, px: 2, "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.12) } }}
        >
          <ListItemIcon sx={{ color: theme.palette.primary.light, minWidth: 30 }}>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Profile"
            secondary="Account and preferences"
            primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "0.75rem", color: "text.secondary" }}
          />
        </MenuItem>

        <Divider sx={{ borderColor: alpha(theme.palette.text.secondary, 0.16) }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ py: 1.25, px: 2, "&:hover": { backgroundColor: alpha(theme.palette.error.main, 0.12) } }}
        >
          <ListItemIcon sx={{ color: theme.palette.error.main, minWidth: 30 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Sign Out"
            secondary="Securely exit this session"
            primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 600, color: theme.palette.error.main }}
            secondaryTypographyProps={{ fontSize: "0.75rem", color: "text.secondary" }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default LoggedInProfileMenuComponent;
